import * as React from 'react';
import { FC, useMemo } from 'react';
import { Form } from 'reactstrap';
import { TextInputField, ComboField, PlaceholderTextField } from '../fields';
import { Row } from '../table/Row';
import { Col } from '../table/Column';
import { InvoiceItemDetails, InvoiceItemType } from '../../../models/api-models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useTaxData } from '../../../hooks/useTaxData';
import { useSelector } from 'react-redux';
import { getInvoiceSettings } from '../../../store/settings/selectors';
import { getSelectedBranch } from '../../../store/general/selectors';
import { hasFeature } from '../../../store/session/selectors';
import { FeatureEnum } from '../../../models/api-models';
import { resolveEffectiveLegalInfo } from '../../../shared/appointmint/invoice';
import {
    ITEM_TYPE_DISPLAY_NAMES,
    ITEM_TYPE_OPTIONS,
    ITEM_TYPE_MAP,
} from '../../../constants/taxMappings';

export interface InvoiceItemDetailsFormProps {
    id: string;
    value: InvoiceItemDetails | null | undefined;
    onChange: (value: InvoiceItemDetails | null | undefined) => void;
    disabled?: boolean;
    parentContext?: 'service' | 'extra';
    parentName?: string;
}

const InvoiceItemDetailsForm: FC<InvoiceItemDetailsFormProps> = ({
    id,
    value,
    onChange,
    disabled = false,
    parentContext = 'extra',
    parentName,
}) => {
    const t = useTranslations();
    const {
        taxRates,
        taxExemptions,
        getTaxDisplayName,
        getTaxIdFromDisplayName,
        isExemptTax,
        isLoading,
        taxExemptionOptionsMap,
        taxExemptionOptions,
    } = useTaxData();
    const invoiceSettings = useSelector(getInvoiceSettings);
    const selectedBranch = useSelector(getSelectedBranch);
    const useBranches = useSelector(hasFeature(FeatureEnum.Branches));

    // Get defaults from settings or first available data
    const getDefaults = useMemo(() => {
        const effectiveLegalInfo = resolveEffectiveLegalInfo(
            invoiceSettings,
            useBranches ? selectedBranch : undefined,
        );
        const defaultType = parentContext === 'service' ? InvoiceItemType.S : InvoiceItemType.P;

        if (effectiveLegalInfo?.taxId !== undefined) {
            const isExempt = isExemptTax(effectiveLegalInfo.taxId);
            return {
                type: defaultType,
                taxId: effectiveLegalInfo.taxId,
                taxExemption: isExempt ? effectiveLegalInfo.taxExemption : undefined,
                taxExemptionLaw: isExempt ? effectiveLegalInfo.taxExemptionLaw : undefined,
            };
        }

        // Fallback to first available tax rate
        if (taxRates.length > 0) {
            const firstTax = taxRates[0];
            const isExempt = firstTax.rate === 0;

            return {
                type: defaultType,
                taxId: firstTax.id,
                taxExemption: isExempt && taxExemptions.length > 0 ? taxExemptions[0].id : undefined,
                taxExemptionLaw: isExempt && taxExemptions.length > 0 ? taxExemptions[0].law?.[0] : undefined,
            };
        }

        return {
            type: defaultType,
            taxId: undefined,
            taxExemption: undefined,
            taxExemptionLaw: undefined,
        };
    }, [invoiceSettings, taxRates, taxExemptions, parentContext, isExemptTax, useBranches, selectedBranch]);

    const data: InvoiceItemDetails = useMemo(() => {
        return {
            customTitle: value != null ? value.customTitle : (parentName || undefined),
            customDescription: value != null ? value.customDescription : (parentName || undefined),
            type: value?.type ?? getDefaults.type,
            taxId: value?.taxId ?? getDefaults.taxId,
            taxExemption: value?.taxExemption ?? getDefaults.taxExemption,
            taxExemptionLaw: value?.taxExemptionLaw ?? getDefaults.taxExemptionLaw,
        };
    }, [value, getDefaults, parentName]);

    return (
        <Form style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
            <Row>
                <Col md={12}>
                    <PlaceholderTextField
                        id={`${id}-custom-title`}
                        label={t(TK.customTitle)}
                        helpText={t(TK.invoiceItemPlaceholdersHelp)}
                        value={data.customTitle ?? ''}
                        disabled={disabled}
                        onChange={customTitle => onChange({
                            ...data,
                            customTitle: customTitle || undefined
                        })}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <PlaceholderTextField
                        id={`${id}-custom-description`}
                        type="textarea"
                        label={t(TK.customDescription)}
                        helpText={t(TK.invoiceItemPlaceholdersHelp)}
                        value={data.customDescription ?? ''}
                        disabled={disabled}
                        onChange={customDescription => onChange({
                            ...data,
                            customDescription: customDescription || undefined
                        })}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <ComboField
                        id={`${id}-item-type`}
                        label={t(TK.itemType)}
                        value={ITEM_TYPE_DISPLAY_NAMES[data.type ?? InvoiceItemType.P] || ITEM_TYPE_DISPLAY_NAMES[InvoiceItemType.P]}
                        options={ITEM_TYPE_OPTIONS}
                        disabled={disabled}
                        onChange={displayValue => {
                            const type = ITEM_TYPE_MAP[displayValue || ITEM_TYPE_DISPLAY_NAMES[InvoiceItemType.P]] ?? InvoiceItemType.P;
                            onChange({
                                ...data,
                                type
                            });
                        }}
                    />
                </Col>
                <Col md={4}>
                    <ComboField
                        id={`${id}-tax-id`}
                        label={t(TK.taxId)}
                        value={getTaxDisplayName(data.taxId)}
                        options={taxRates.map(r => r.title?.replace(/^IVA\s*-\s*/i, '') || '').filter(Boolean)}
                        disabled={disabled}
                        onChange={displayValue => {
                            const taxId = getTaxIdFromDisplayName(displayValue || '');
                            const isExempt = isExemptTax(taxId);

                            onChange({
                                ...data,
                                taxId,
                                taxExemption: isExempt ? data.taxExemption : undefined,
                                taxExemptionLaw: isExempt ? data.taxExemptionLaw : undefined
                            });
                        }}
                    />
                </Col>
            </Row>
            {isExemptTax(data.taxId) && (
                <Row>
                    <Col md={6}>
                        <ComboField
                            id={`${id}-tax-exemption`}
                            label={t(TK.taxExemption)}
                            value={data.taxExemption ? (taxExemptionOptionsMap[data.taxExemption] || data.taxExemption) : ''}
                            options={taxExemptionOptions}
                            disabled={disabled || isLoading}
                            onChange={displayValue => {
                                const exemptionId = displayValue?.substring(0, 3) || '';
                                const exemptionObj = taxExemptions.find(e => e.id === exemptionId);
                                const law = exemptionObj?.law?.[0] || undefined;

                                onChange({
                                    ...data,
                                    taxExemption: exemptionId || undefined,
                                    taxExemptionLaw: law,
                                });
                            }}
                        />
                    </Col>
                    <Col md={6}>
                        <TextInputField
                            id={`${id}-tax-exemption-law`}
                            label={t(TK.taxExemptionLaw)}
                            value={data.taxExemptionLaw || ''}
                            type="text"
                            disabled={disabled}
                            onChange={taxExemptionLaw => onChange({
                                ...data,
                                taxExemptionLaw: taxExemptionLaw || undefined
                            })}
                            placeholder="Artigo 9.º do CIVA"
                        />
                    </Col>
                </Row>
            )}
        </Form>
    );
};

export default InvoiceItemDetailsForm;
